import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import flatten from 'ramda/es/flatten'
import uniqBy from 'ramda/es/uniqBy'
import isNil from 'ramda/es/isNil'
import curry from 'ramda/es/curry'
import pipe from 'ramda/es/pipe'
import replace from 'ramda/es/replace'
import any from 'ramda/es/any'
import { loadPiniaSlice, persistPiniaSlice } from '../persist'
import { scheduleTestSessionPatch } from '../testSession'

const uniqByID = uniqBy((sc) => (sc && sc.id != null ? sc.id : null))

const buildAllKnownSubCategories = (allLoadedSubCategories, allSubCategories, selectedSubCategories) =>
    flatten(allLoadedSubCategories.map((c) => c.subCategories))
        .concat(allSubCategories || [])
        .concat(selectedSubCategories || [])

export const useSubCategoriesStore = defineStore('subCategories', () => {
    const allSubCategories = ref([])
    const allLoadedSubCategories = ref([])
    const selectedSubCategories = ref([])
    const selectedSubCategory = ref(undefined)

    const findSubCategoryById = (id) => {
        if (isNil(id)) return undefined

        return uniqByID(
            buildAllKnownSubCategories(
                allLoadedSubCategories.value,
                allSubCategories.value,
                selectedSubCategories.value
            )
        ).find((c) => c && c.id != null && c.id.toString() === id.toString())
    }

    const doCategoriesExistFor = (mainCategoryID) => {
        const entry = allLoadedSubCategories.value.find(
            (x) => x.mainCategoryID == mainCategoryID
        )

        return (
            !isNil(entry) &&
            entry.subCategories &&
            entry.subCategories.length > 0 &&
            entry.subCategories[0] &&
            Object.keys(entry.subCategories[0]).length > 0
        )
    }

    const getAllLoadedSubCategoriesFor = (mainCategoryID) => {
        const result = allLoadedSubCategories.value.find(
            (x) => x.mainCategoryID == mainCategoryID
        )
        return result ? result.subCategories : []
    }

    const allQuestionsAtOnceInSubCategory = computed(() => {
        return !selectedSubCategory.value
            ? false
            : selectedSubCategory.value.aufgaben_anzeige == 'zusammen'
    })

    const resetAllInSubCategories = () => {
        allSubCategories.value = []
        allLoadedSubCategories.value = []
        selectedSubCategories.value = []
        persistToLocalStorage()
    }

    const setAllSubCategories = (subCategories) => {
        allSubCategories.value = subCategories
        persistToLocalStorage()
    }

    const mergeKnownSubCategories = (subCategories) => {
        if (!Array.isArray(subCategories) || subCategories.length === 0)
            return

        allSubCategories.value = uniqByID(allSubCategories.value.concat(subCategories))
        persistToLocalStorage()
    }

    const addToLoadedSubCategories = (payload) => {
        if (doCategoriesExistFor(payload.mainCategoryID)) return

        allLoadedSubCategories.value.push({
            mainCategoryID: payload.mainCategoryID,
            subCategories: payload.subCategories,
        })
        persistToLocalStorage()
    }

    const initSubCategories = (subcategories) => {
        const clean = pipe(replace('„', ''), replace('“', ''), replace('"', ''))
        const sort = (scs) =>
            scs.sort((a, b) => clean(a.name).localeCompare(clean(b.name)))
        selectedSubCategories.value = sort(subcategories ? subcategories : [])

        const inCollection = any(
            (sc) => Number(sc.id) === Number(selectedSubCategory.value.id)
        )
        if (selectedSubCategory.value && inCollection(selectedSubCategories.value))
            return

        selectedSubCategory.value = {}
        persistToLocalStorage()
    }

    const setNumberOfQuestionsOfSubCategory = (data) => {
        const setNumberOfQuestions = curry((questionData, subCat) => {
            subCat.aufgaben_anzahl = questionData.numberOfQuestions
            subCat.richtigeFragenAnzahl = questionData.numberOfCorrectQuestions
            subCat.falscheFragenAnzahl = questionData.numberOfWrongQuestions
            subCat.unbearbeiteteFragenAnzahl = questionData.numberOfUneditedQuestions
        })
        const findSubCategory = curry((subCatID, subCategories) =>
            subCategories.find((sc) => sc.id == subCatID)
        )
        const processToSetNumberOfQuestions = pipe(
            findSubCategory(data.id),
            setNumberOfQuestions(data)
        )

        processToSetNumberOfQuestions(allSubCategories.value)

        if (selectedSubCategory.value.id == data.id)
            setNumberOfQuestions(data, selectedSubCategory.value)

        persistToLocalStorage()
    }

    const selectSubCategory = (subCategory) => {
        selectedSubCategory.value = subCategory

        if (
            selectedSubCategory.value.beispielaufgabe &&
            Array.isArray(selectedSubCategory.value.beispielaufgabe.antworten)
        )
            selectedSubCategory.value.beispielaufgabe.antworten.sort()

        persistToLocalStorage()
    }

    const selectSubCategoryById = (id) => {
        let subCat = findSubCategoryById(id)

        if (!subCat)
            subCat = selectedSubCategories.value.find((sc) => sc.id === id)

        selectedSubCategory.value = subCat
        persistToLocalStorage()
    }

    const persistToLocalStorage = () => {
        persistPiniaSlice('subCategoriesStore', {
            selectedSubCategories: selectedSubCategories.value,
            selectedSubCategory: selectedSubCategory.value,
        })
        scheduleTestSessionPatch({
            allLoadedSubCategories: allLoadedSubCategories.value,
            allSubCategories: allSubCategories.value,
            selectedSubCategories: selectedSubCategories.value,
            selectedSubCategory: selectedSubCategory.value,
        })
    }

    const hydrateCatalog = (session) => {
        if (Array.isArray(session.allLoadedSubCategories)) {
            allLoadedSubCategories.value = session.allLoadedSubCategories
        }
        if (Array.isArray(session.allSubCategories) && allSubCategories.value.length === 0) {
            allSubCategories.value = session.allSubCategories
        }
        if (Array.isArray(session.selectedSubCategories) && selectedSubCategories.value.length === 0) {
            selectedSubCategories.value = session.selectedSubCategories
        }
        if (session.selectedSubCategory !== undefined && selectedSubCategory.value === undefined) {
            selectedSubCategory.value = session.selectedSubCategory
        }
    }

    const loadFromLocalStorage = () => {
        const stored = loadPiniaSlice('subCategoriesStore')
        if (!stored) return
        if (Array.isArray(stored.selectedSubCategories)) {
            selectedSubCategories.value = stored.selectedSubCategories
        }
        if (stored.selectedSubCategory !== undefined) {
            selectedSubCategory.value = stored.selectedSubCategory
        }
    }

    loadFromLocalStorage()

    return {
        allSubCategories,
        allLoadedSubCategories,
        selectedSubCategories,
        selectedSubCategory,
        findSubCategoryById,
        doCategoriesExistFor,
        getAllLoadedSubCategoriesFor,
        allQuestionsAtOnceInSubCategory,
        resetAllInSubCategories,
        setAllSubCategories,
        mergeKnownSubCategories,
        addToLoadedSubCategories,
        initSubCategories,
        setNumberOfQuestionsOfSubCategory,
        selectSubCategory,
        selectSubCategoryById,
        hydrateCatalog,
    }
})
